import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, List, ListItem, ListItemText, IconButton, Switch, FormControlLabel, Stack } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import notificationsApi from '../../services/api/notifications-advanced.api';

interface Category {
  id: string;
  key: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const CategoriesManager: React.FC<Props> = ({ open, onClose }) => {
  const [items, setItems] = useState<Category[]>([]);
  const [keyValue, setKeyValue] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.getCategories();
      const data = (res as any).data || res;
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleCreate = async () => {
    if (!keyValue || !name) return;
    setLoading(true);
    try {
      const payload: Parameters<typeof notificationsApi.createCategory>[0] = { key: keyValue, name, isActive: true };
      if (description != null && description !== '') payload.description = description;
      await notificationsApi.createCategory(payload);
      setKeyValue(''); setName(''); setDescription('');
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (c: Category) => {
    setLoading(true);
    try {
      const payload: Parameters<typeof notificationsApi.updateCategory>[1] = {};
      if (c.key !== undefined) payload.key = c.key;
      if (c.name !== undefined) payload.name = c.name;
      if (c.description !== undefined) payload.description = c.description;
      if (c.isActive !== undefined) payload.isActive = c.isActive;
      await notificationsApi.updateCategory(c.id, payload);
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await notificationsApi.deleteCategory(id);
      await load();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Категории уведомлений</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ my: 1 }}>
          <TextField label="Ключ" size="small" value={keyValue} onChange={e => setKeyValue(e.target.value)} sx={{ width: 160 }} />
          <TextField label="Название" size="small" value={name} onChange={e => setName(e.target.value)} fullWidth />
        </Stack>
        <TextField label="Описание" size="small" value={description} onChange={e => setDescription(e.target.value)} fullWidth sx={{ mb: 1 }} />
        <Button variant="contained" onClick={handleCreate} startIcon={<AddIcon />} disabled={loading || !keyValue || !name}>Добавить</Button>

        <List>
          {items.map((c, idx) => (
            <ListItem key={c.id} secondaryAction={
              <Box>
                <FormControlLabel control={<Switch checked={c.isActive} onChange={(e) => { items[idx] = { ...c, isActive: e.target.checked }; setItems([...items]); }} />} label="Активна" />
                <IconButton edge="end" onClick={() => { const cat = items[idx]; if (cat) handleUpdate(cat); }} disabled={loading}><SaveIcon /></IconButton>
                <IconButton edge="end" onClick={() => handleDelete(c.id)} disabled={loading}><DeleteIcon /></IconButton>
              </Box>
            }>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" label="Ключ" defaultValue={c.key} onBlur={e => { items[idx] = { ...c, key: e.target.value }; }} sx={{ width: 160 }} />
                    <TextField size="small" label="Название" defaultValue={c.name} onBlur={e => { items[idx] = { ...c, name: e.target.value }; }} fullWidth />
                  </Stack>
                }
                secondary={<TextField size="small" label="Описание" defaultValue={c.description || ''} onBlur={e => { items[idx] = { ...c, description: e.target.value }; }} fullWidth />}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoriesManager;


